import React from "react";
import { Card, Tag, Button, Row, Col, Tooltip } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  PlayCircleOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  PhoneOutlined
} from "@ant-design/icons";
import WorkInProgress from "../WorkInProgress";
import "./index.less";

const JobCardSections = ({
  jobCard,
  currentUser,
  onAssignMechanic,
  onOpenClaimIntimation,
  onOpenEstimate,
  onOpenSparesOrder,
  onStartWork,
  onWorkStatusChange,
}) => {
  if (!jobCard) return null;

  const isAccidental =
    jobCard?.serviceType && jobCard.serviceType.toLowerCase().includes("accidental");
  const status = jobCard?.jobStatus || "Vehicle Received";
  const mechanicName =
    jobCard?.User_JobOrder_mechanicToUser?.username ||
    jobCard?.User_JobOrder_mechanicToUser?.name ||
    jobCard?.mechanic;

  // Determine section active states
  const getSectionState = (sectionId) => {
    switch (sectionId) {
      case "VR": {
        return { active: true, completed: status !== "Vehicle Received" && status !== "Mechanic Allocated" };
      }

      case "CLAIM": {
        if (!isAccidental) return { disabled: true };
        const inClaim = status === "Claim Intimated" || status === "Surveyor Appointed";
        const pastClaim = [
          "Spares Ordered",
          "Spares Received",
          "Spares Partially Received",
          "Work In Progress",
          "Work Completed",
          "Final Inspection",
          "Washing",
          "Washing and Final Inspection",
          "Proforma Invoice",
          "Payment Completed",
          "Ready for Delivery",
          "Feedback Call",
          "Closed",
        ].includes(status);
        return { active: inClaim || pastClaim, completed: pastClaim };
      }

      case "SPARES": {
        const inSpares = [
          "Spares Ordered",
          "Spares Received",
          "Spares Partially Received",
        ].includes(status);
        const pastSpares = [
          "Work In Progress",
          "Work Completed",
          "Final Inspection",
          "Washing",
          "Washing and Final Inspection",
          "Proforma Invoice",
          "Payment Completed",
          "Ready for Delivery",
          "Feedback Call",
          "Closed",
        ].includes(status);
        return { active: inSpares || pastSpares, completed: pastSpares };
      }

      case "WIP": {
        const inWip = status === "Work In Progress";
        const pastWip = [
          "Final Inspection",
          "Washing",
          "Washing and Final Inspection",
          "Work Completed",
          "Proforma Invoice",
          "Payment Completed",
          "Ready for Delivery",
          "Feedback Call",
          "Closed",
        ].includes(status);
        return { active: inWip || pastWip, completed: pastWip };
      }

      case "COMPLETED": {
        const inComp = [
          "Final Inspection",
          "Washing",
          "Washing and Final Inspection",
          "Work Completed",
          "Proforma Invoice",
        ].includes(status);
        const pastComp = [
          "Payment Completed",
          "Ready for Delivery",
          "Feedback Call",
          "Closed",
        ].includes(status);
        return { active: inComp || pastComp, completed: pastComp };
      }

      case "READY": {
        const inReady = [
          "Ready for Delivery",
          "Payment Completed",
          "Gate Pass",
        ].includes(status);
        const pastReady = ["Delivered", "Feedback Call", "Closed"].includes(status);
        return { active: inReady || pastReady, completed: pastReady };
      }

      case "FEEDBACK": {
        const inFeedback = status === "Delivered" || status === "Feedback Call" || status === "Closed";
        return { active: inFeedback, completed: status === "Closed" };
      }

      default: {
        return { active: false, completed: false };
      }
    }
  };

  const vrState = getSectionState("VR");
  const claimState = getSectionState("CLAIM");
  const sparesState = getSectionState("SPARES");
  const wipState = getSectionState("WIP");
  const compState = getSectionState("COMPLETED");
  const readyState = getSectionState("READY");
  const feedbackState = getSectionState("FEEDBACK");

  return (
    <div className="jobcard-sections-container">
      {/* Header Banner */}
      <div className="jobcard-header-banner">
        <div className="banner-left">
          <h3>JobCard #{jobCard.jobNo || jobCard.id}</h3>
          <Tag color={isAccidental ? "volcano" : "blue"}>
            {jobCard.serviceType || "Regular Service"}
          </Tag>
          <Tag color="cyan">Status: {status}</Tag>
        </div>
        <div className="banner-right">
          {mechanicName ? (
            <span className="mechanic-badge">
              <UserOutlined /> Mechanic: <strong>{mechanicName}</strong>
            </span>
          ) : (
            <span className="mechanic-unassigned">No Mechanic Assigned</span>
          )}
        </div>
      </div>

      {/* 7 Section Flow Cards */}
      <div className="sections-flow-wrapper">
        {/* SECTION 1: Vehicle Received */}
        <div className={`section-card ${vrState.completed ? "completed" : "active"}`}>
          <div className="section-header">
            <h4>Vehicle Received</h4>
            {vrState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : (
              <ClockCircleOutlined className="status-icon active" />
            )}
          </div>
          <div className="section-content">
            <div className="checkpoints-list">
              <div className="cp-item done">✓ Vehicle Checked In</div>
              {mechanicName && (
                <div className="cp-item done">✓ Mechanic Allocated ({mechanicName})</div>
              )}
            </div>
            <div className="section-actions">
              {onAssignMechanic && (
                <Button size="small" type="default" icon={<UserOutlined />} onClick={onAssignMechanic}>
                  {mechanicName ? "Re-Assign Mechanic" : "Assign Mechanic"}
                </Button>
              )}
              {onOpenEstimate && (
                <Button size="small" icon={<FileTextOutlined />} onClick={onOpenEstimate}>
                  Estimate
                </Button>
              )}
              {onOpenSparesOrder && (
                <Button size="small" icon={<ShoppingCartOutlined />} onClick={onOpenSparesOrder}>
                  Order Spares
                </Button>
              )}
              {onStartWork &&
                jobCard?.mechanic &&
                !jobCard?.timerRunning &&
                (status === "Mechanic Allocated" ||
                  (jobCard?.elapsedSeconds > 0 &&
                    [
                      "Vehicle Received",
                      "Claim Intimated",
                      "Surveyor Appointed",
                      "Estimation",
                      "Estimation Approved",
                      "Spares Ordered",
                      "Spares Partially Ordered",
                      "Spare Received",
                      "Spares Received",
                    ].includes(status)) ||
                  [
                    "Work In Progress",
                    "Parts Issued",
                    "Labour Performed",
                    "Material Issued",
                  ].includes(status)) && (
                <Button
                  size="small"
                  type="primary"
                  className="start-work-btn"
                  icon={<PlayCircleOutlined />}
                  onClick={onStartWork}
                >
                  Start Work
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 2: Claim Intimated (Accidental Only) */}
        {isAccidental && (
          <div className={`section-card ${claimState.completed ? "completed" : claimState.active ? "active" : "disabled"}`}>
            <div className="section-header">
              <h4>Claim Intimated</h4>
              {claimState.completed ? (
                <CheckCircleFilled className="status-icon done" />
              ) : claimState.active ? (
                <ClockCircleOutlined className="status-icon active" />
              ) : null}
            </div>
            <div className="section-content">
              <div className="checkpoints-list">
                {jobCard?.ClaimIntimation ? (
                  <>
                    <div className="cp-item done">
                      ✓ Claim Intimated ({jobCard.ClaimIntimation.claimType})
                    </div>
                    {jobCard.ClaimIntimation.surveyor && (
                      <div className="cp-item done">✓ Surveyor Appointed</div>
                    )}
                  </>
                ) : (
                  <div className="cp-item pending">Claim details pending</div>
                )}
              </div>
              <div className="section-actions">
                {onOpenClaimIntimation && (
                  <Button size="small" type="primary" ghost icon={<FileTextOutlined />} onClick={onOpenClaimIntimation}>
                    Claim Intimation
                  </Button>
                )}
                {onOpenEstimate && (
                  <Button size="small" icon={<FileTextOutlined />} onClick={onOpenEstimate}>
                    Estimate
                  </Button>
                )}
                {onOpenSparesOrder && (
                  <Button size="small" icon={<ShoppingCartOutlined />} onClick={onOpenSparesOrder}>
                    Order Spares
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3: Spares Ordered */}
        <div className={`section-card ${sparesState.completed ? "completed" : sparesState.active ? "active" : "pending"}`}>
          <div className="section-header">
            <h4>Spares Ordered</h4>
            {sparesState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : sparesState.active ? (
              <ClockCircleOutlined className="status-icon active" />
            ) : null}
          </div>
          <div className="section-content">
            <div className="checkpoints-list">
              {["Spares Ordered", "Spares Received", "Spares Partially Received"].includes(status) ? (
                <div className="cp-item done">
                  Status: <strong>{status}</strong>
                </div>
              ) : (
                <div className="cp-item pending">No active spare order</div>
              )}
            </div>
            <div className="section-actions">
              {onOpenSparesOrder && (
                <Button size="small" icon={<ShoppingCartOutlined />} onClick={onOpenSparesOrder}>
                  Manage Spares
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 4: Work In Progress */}
        <div className={`section-card ${wipState.completed ? "completed" : wipState.active ? "active" : "pending"}`}>
          <div className="section-header">
            <h4>Work In Progress</h4>
            {wipState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : wipState.active ? (
              <ClockCircleOutlined className="status-icon active" />
            ) : null}
          </div>
          <div className="section-content">
            {wipState.active || status === "Work In Progress" ? (
              <WorkInProgress
                jobCard={jobCard}
                currentUser={currentUser}
                onWorkStatusChange={onWorkStatusChange}
              />
            ) : (
              <div className="cp-item pending">Work timer not started yet</div>
            )}
          </div>
        </div>

        {/* SECTION 5: Work Completed */}
        <div className={`section-card ${compState.completed ? "completed" : compState.active ? "active" : "pending"}`}>
          <div className="section-header">
            <h4>Work Completed</h4>
            {compState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : compState.active ? (
              <ClockCircleOutlined className="status-icon active" />
            ) : null}
          </div>
          <div className="section-content">
            {compState.active || compState.completed ? (
              <WorkInProgress
                jobCard={jobCard}
                currentUser={currentUser}
                isCompleted={true}
                onWorkStatusChange={onWorkStatusChange}
              />
            ) : (
              <div className="cp-item pending">Final Inspection & Fitting</div>
            )}
          </div>
        </div>

        {/* SECTION 6: Ready for Delivery */}
        <div className={`section-card ${readyState.completed ? "completed" : readyState.active ? "active" : "pending"}`}>
          <div className="section-header">
            <h4>Ready for Delivery</h4>
            {readyState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : readyState.active ? (
              <ClockCircleOutlined className="status-icon active" />
            ) : null}
          </div>
          <div className="section-content">
            <div className="cp-item pending">Invoice & Delivery Gate Pass</div>
          </div>
        </div>

        {/* SECTION 7: Feedback Call */}
        <div className={`section-card ${feedbackState.completed ? "completed" : feedbackState.active ? "active" : "pending"}`}>
          <div className="section-header">
            <h4>Feedback Call</h4>
            {feedbackState.completed ? (
              <CheckCircleFilled className="status-icon done" />
            ) : feedbackState.active ? (
              <ClockCircleOutlined className="status-icon active" />
            ) : null}
          </div>
          <div className="section-content">
            <div className="cp-item pending">Post-service customer feedback</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCardSections;
